import { auth } from './firebase'

export interface StoryCreationEvent {
  userId: string
  childId: string
  sessionId?: string
  theme: string
  inputMethod: 'tell' | 'write' | 'show'
  characterIds: string[]
  mainCharacterId?: string
  language: string
  timestamp: Date
  deviceType?: string
}

export interface StoryReadingEvent {
  userId: string
  childId: string
  storyId: string
  startTime: Date
  endTime?: Date
  duration?: number // in seconds
  ttsUsed: boolean
  completed: boolean
  timestamp: Date
}

export interface StoryRatingEvent {
  userId: string
  childId: string
  storyId: string
  rating: number // 1-5
  timestamp: Date
}

export interface ProfileUsageEvent {
  userId: string
  childId: string
  action: 'switched' | 'created' | 'edited' | 'deleted'
  timestamp: Date
}

export interface ErrorEvent {
  userId?: string
  error: string
  context: string
  userAgent: string
  timestamp: Date
}

class AnalyticsService {
  private readingSessions: Map<string, StoryReadingEvent> = new Map()
  private isEnabled: boolean = import.meta.env.VITE_ENABLE_ANALYTICS === 'true'

  // Get device type
  private getDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase()
    if (/ipad/.test(userAgent)) return 'iPad'
    if (/iphone/.test(userAgent)) return 'iPhone'
    if (/android/.test(userAgent)) return 'Android'
    if (/mobile/.test(userAgent)) return 'Mobile'
    return 'Desktop'
  }

  private async sendAnalyticsEvent(eventType: string, data: any) {
    if (!this.isEnabled) {
      return
    }

    try {
      // In the future, this could send to a backend analytics endpoint
      // await api.sendAnalytics(eventType, data);

      // For now, just log to console

      // Store in localStorage for potential later sync
      const analyticsQueue = JSON.parse(localStorage.getItem('analytics_queue') || '[]')
      analyticsQueue.push({
        eventType,
        data,
        timestamp: new Date().toISOString(),
      })

      // Keep only last 100 events to avoid storage bloat
      if (analyticsQueue.length > 100) {
        analyticsQueue.splice(0, analyticsQueue.length - 100)
      }

      localStorage.setItem('analytics_queue', JSON.stringify(analyticsQueue))
    } catch (error) {
      //console.error('Failed to send analytics event:', error);
    }
  }

  // Track story creation
  async trackStoryCreation(data: Omit<StoryCreationEvent, 'userId' | 'timestamp' | 'deviceType'>) {
    const user = auth.currentUser
    if (!user) return

    const event: StoryCreationEvent = {
      ...data,
      userId: user.uid,
      deviceType: this.getDeviceType(),
      timestamp: new Date(),
    }

    await this.sendAnalyticsEvent('storyCreation', event)
  }

  // Start reading session
  async startReadingSession(storyId: string, childId: string, ttsUsed: boolean = false) {
    const user = auth.currentUser
    if (!user) return null

    const sessionId = `${storyId}_${Date.now()}`
    const session: StoryReadingEvent = {
      userId: user.uid,
      childId,
      storyId,
      startTime: new Date(),
      ttsUsed,
      completed: false,
      timestamp: new Date(),
    }

    this.readingSessions.set(sessionId, session)
    return sessionId
  }

  // End reading session
  async endReadingSession(sessionId: string, completed: boolean = false) {
    const session = this.readingSessions.get(sessionId)
    if (!session) return

    const endTime = new Date()
    const duration = Math.round((endTime.getTime() - session.startTime.getTime()) / 1000)

    const completedSession = {
      ...session,
      endTime,
      duration,
      completed,
    }

    await this.sendAnalyticsEvent('storyReading', completedSession)

    this.readingSessions.delete(sessionId)
  }

  // Track story rating
  async trackStoryRating(storyId: string, childId: string, rating: number) {
    const user = auth.currentUser
    if (!user) return

    const event: StoryRatingEvent = {
      userId: user.uid,
      childId,
      storyId,
      rating,
      timestamp: new Date(),
    }

    await this.sendAnalyticsEvent('storyRating', event)
  }

  // Track profile usage
  async trackProfileUsage(childId: string, action: ProfileUsageEvent['action']) {
    const user = auth.currentUser
    if (!user) return

    const event: ProfileUsageEvent = {
      userId: user.uid,
      childId,
      action,
      timestamp: new Date(),
    }

    await this.sendAnalyticsEvent('profileUsage', event)
  }

  // Track errors
  async trackError(error: string, context: string) {
    const user = auth.currentUser

    const event: ErrorEvent = {
      userId: user?.uid,
      error: error.toString(),
      context,
      userAgent: navigator.userAgent,
      timestamp: new Date(),
    }

    await this.sendAnalyticsEvent('error', event)
  }

  // Get analytics queue for debugging or sync
  getAnalyticsQueue() {
    return JSON.parse(localStorage.getItem('analytics_queue') || '[]')
  }

  // Clear analytics queue
  clearAnalyticsQueue() {
    localStorage.removeItem('analytics_queue')
  }

  // Enable/disable analytics
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled
  }
}

export const analytics = new AnalyticsService()
export default analytics
